import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';
import { applyModuleSeo } from '../../utils/seo';

const CANONICAL = 'https://explanatum.co.uk/modules/investor-crm';

const InvestorCrmLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Investor CRM & Fundraising Hub | Explanatum';
    const desc = 'Centralise investor outreach, track pipeline stages, and automate follow-ups—without brittle spreadsheets or hiring investor finder agencies.';
    applyModuleSeo({
      title,
      description: desc,
      canonicalUrl: CANONICAL,
      keywords: [
        'investor crm software',
        'fundraising pipeline tracker',
        'investor relations automation',
        'startup fundraising hub',
        'explanatum investor crm'
      ],
      structuredDataId: 'ld-module-investor-crm',
      softwareName: 'AI Investor CRM & Fundraising Hub',
      softwareCategory: 'FinanceApplication'
    });
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Investor CRM overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 px-4 py-1 text-sm font-semibold text-amber-100">
                <span className="inline-flex h-2 w-2 rounded-full bg-amber-300" />
                AI Investor CRM & Fundraising Hub
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Orchestrate every investor touchpoint from one command center.
                <span className="mt-2 block text-blue-600">
                  Pipeline stages, intro notes, and diligence updates—all synced in real time.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Capture investor intel, manage warm intros, and monitor capital commitments without spreadsheet chaos or outsourced investor finder fees. Explanatum keeps founders, finance teams, and advisors aligned on every fundraising conversation.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_investor_crm_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-amber-500 via-sky-500 to-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-amber-500/30 transition hover:scale-[1.02]"
                >
                  Start 7-day trial
                </a>
                <a
                  href="/investor-crm"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Open investor workspace
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Fundraising pipeline with stages from prospecting to close
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-300" />
                  Investor directory enriched with check sizes, focus, and HQ
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Quick actions, alerts, and AI notes to progress deals faster
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Investor CRM preview">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-amber-500/30 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl ">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Fundraising pulse</span>
                  <span>Live insights</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Pipeline 42 investors • 9 warm intros • 3 term sheets • AI highlights next best actions for diligence.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Top funds filtered by cheque size £750k–£2M and SaaS focus • Metrics auto-update for team stand-ups.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Investor briefs exported for board review with notes, stage changes, and capital probability trends.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Key benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Fundraising rigor without spreadsheet overhead</h2>
            <p className="mt-4 text-base text-gray-500">Give every stakeholder the same source of truth—from first intro to signed term sheet.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'Investor intelligence database',
              body: 'Store contact data, cheque sizes, sector focus, and HQ details so every outreach is contextual.'
            }, {
              title: 'Pipeline & stage tracking',
              body: 'Visualise progress across prospecting, diligence, and close with automated metrics for leadership.'
            }, {
              title: 'Warm intro management',
              body: 'Log referrers, mutual connections, and follow-up tasks to keep momentum across conversations.'
            }, {
              title: 'Follow-up automation',
              body: 'Trigger reminders, send AI-drafted updates, and ensure no investor sits idle in the funnel.'
            }, {
              title: 'Board-ready reporting',
              body: 'Export decks and summaries with live data, notes, and confidence scores ahead of investor updates.'
            }, {
              title: 'Consultant-level insights',
              body: 'Pair with the AI Business Consultant for diligence prep, objection handling, and capital strategy memos.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why founders upgrade from spreadsheets and investor finders</h2>
            <p className="mt-4 text-base text-gray-500">Operate a repeatable fundraising machine without paying retainers or rebuilding data every round.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum Investor CRM & Fundraising Hub',
              points: [
                'Unified investor directory with rich profiles and filters',
                'Pipeline stages, metrics, and alerts in one live workspace',
                'AI-generated updates, follow-ups, and prep checklists',
                'Collaboration for founders, CFOs, and advisors simultaneously',
                'Predictable subscription—launch in minutes, not months'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Manual spreadsheets & notebooks',
              points: [
                'Fragmented investor data with outdated contact info',
                'No automated reminders or probability insights',
                'Difficult to share context across founders and advisors',
                'Version conflicts erode confidence before board reviews',
                'Significant time lost compiling updates for each meeting'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Investor finder agencies & consultants',
              points: [
                '£5,000+ retainers plus success fees on capital raised',
                'Limited transparency into pipeline and warm intros',
                'Insights delivered as static reports, not living systems',
                'Dependent on third parties to nurture relationships',
                'Knowledge disappears once the engagement ends'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-amber-400/30 bg-amber-400/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">“Need an organised investor pipeline before next quarter?”</h3>
            <p className="mt-3 text-base text-gray-600">Import contacts, assign stages, and share AI-crafted updates today—without waiting on agencies or manual clean-up.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_investor_crm_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-amber-500 via-sky-500 to-indigo-500 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-amber-400/30 hover:scale-[1.02]"
              >
                Launch investor CRM now
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with AI Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where investor CRM discipline accelerates capital</h2>
            <p className="mt-4 text-base text-gray-500">Keep fundraising momentum across stages, industries, and geographies.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Pre-seed & seed founders',
              body: 'Organise angel and micro-VC outreach, track intros, and prep AI-backed follow-ups in minutes.'
            }, {
              title: 'Series A–C scale-ups',
              body: 'Coordinate multi-investor diligence with live metrics, document requests, and board-ready dashboards.'
            }, {
              title: 'Venture studios & advisors',
              body: 'Manage multiple client pipelines, capture warm intros, and report progress without rebuilding spreadsheets.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default InvestorCrmLanding;
